"use client";

import { useRef } from "react";
import type {
  SessionLiveSignals,
  UseSessionLiveSignalsInput,
} from "./session-live-signals.types";
import { useSessionCollabLiveSignals } from "./use-session-collab-live-signals";
import { useSessionLiveFlashes } from "./use-session-live-flashes";
import { useSessionLiveProductScope } from "./use-session-live-product-scope";
import { useSessionLiveSignalLifecycle } from "./use-session-live-signal-lifecycle";
import { useSessionMemberActivitySignals } from "./use-session-member-activity-signals";
import { useSessionMemberChangeLiveSignals } from "./use-session-member-change-live-signals";
import { useSessionProductDetailLiveObserver } from "./use-session-product-detail-live-observer";
import { useSessionProductMergeLiveSignals } from "./use-session-product-merge-live-signals";
import { useSessionReflectionLiveObserver } from "./use-session-reflection-live-observer";

export type { SessionLiveSignals } from "./session-live-signals.types";

export function useSessionLiveSignals(
  input: UseSessionLiveSignalsInput,
): SessionLiveSignals {
  const initialLoadCompletedAtRef = useRef<number | null>(null);
  const userAttachmentsProductIdRef = useRef<string | null>(null);
  const productScope = useSessionLiveProductScope({
    products: input.products,
    productsRef: input.productsRef,
    rootProductIdRef: input.rootProductIdRef,
    userAttachmentsProductIdRef,
  });
  const liveFlashSignals = useSessionLiveFlashes({
    initialLoadCompletedAtRef,
    isInUserAttachmentsSubtree: productScope.isInUserAttachmentsSubtree,
  });
  const memberActivitySignals = useSessionMemberActivitySignals({ initialLoadCompletedAtRef });
  const mergeProductsAndFlagChanges = useSessionProductMergeLiveSignals({
    productsRef: input.productsRef,
    userAttachmentsProductIdRef,
    setProducts: input.setProducts,
    getAncestorChain: productScope.getAncestorChain,
    isInUserAttachmentsSubtree: productScope.isInUserAttachmentsSubtree,
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
  });
  const productDetailObserver = useSessionProductDetailLiveObserver({
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
  });
  const reflectionObserver = useSessionReflectionLiveObserver({
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
    pushMemberActivity: memberActivitySignals.pushMemberActivity,
  });
  const flagMemberChanges = useSessionMemberChangeLiveSignals({
    membersRef: input.membersRef,
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
  });
  const collabSignals = useSessionCollabLiveSignals({
    ownMessageIdsRef: input.ownMessageIdsRef,
    getAncestorChain: productScope.getAncestorChain,
    isInUserAttachmentsSubtree: productScope.isInUserAttachmentsSubtree,
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
    pushMemberActivity: memberActivitySignals.pushMemberActivity,
  });
  const lifecycleSignals = useSessionLiveSignalLifecycle({
    initialLoadCompletedAtRef,
    userAttachmentsProductIdRef,
    resetLiveFlashes: liveFlashSignals.resetLiveFlashes,
    resetMemberActivity: memberActivitySignals.resetMemberActivity,
    resetProductDetailObserver: productDetailObserver.resetProductDetailObserver,
    resetReflectionObserver: reflectionObserver.resetReflectionObserver,
    cleanupLiveFlashTimers: liveFlashSignals.cleanupLiveFlashTimers,
    cleanupMemberActivityTimers: memberActivitySignals.cleanupMemberActivityTimers,
  });

  return selectSessionLiveSignals({
    liveFlashSignals, memberActivitySignals, lifecycleSignals,
    mergeProductsAndFlagChanges, productDetailObserver, reflectionObserver,
    flagMemberChanges, collabSignals,
  });
}

interface SelectSessionLiveSignalsInput {
  liveFlashSignals: ReturnType<typeof useSessionLiveFlashes>;
  memberActivitySignals: ReturnType<typeof useSessionMemberActivitySignals>;
  lifecycleSignals: ReturnType<typeof useSessionLiveSignalLifecycle>;
  mergeProductsAndFlagChanges: SessionLiveSignals["mergeProductsAndFlagChanges"];
  productDetailObserver: ReturnType<typeof useSessionProductDetailLiveObserver>;
  reflectionObserver: ReturnType<typeof useSessionReflectionLiveObserver>;
  flagMemberChanges: SessionLiveSignals["flagMemberChanges"];
  collabSignals: ReturnType<typeof useSessionCollabLiveSignals>;
}

function selectSessionLiveSignals({
  liveFlashSignals,
  memberActivitySignals,
  lifecycleSignals,
  mergeProductsAndFlagChanges,
  productDetailObserver,
  reflectionObserver,
  flagMemberChanges,
  collabSignals,
}: SelectSessionLiveSignalsInput): SessionLiveSignals {
  return {
    liveFlashes: liveFlashSignals.liveFlashes,
    memberActivity: memberActivitySignals.memberActivity,
    canFlashForPoll: liveFlashSignals.canFlashForPoll,
    openInitialLoadGate: liveFlashSignals.openInitialLoadGate,
    resetLiveSignals: lifecycleSignals.resetLiveSignals,
    cleanupLiveSignalTimers: lifecycleSignals.cleanupLiveSignalTimers,
    mergeProductsAndFlagChanges,
    flagLiveFlash: liveFlashSignals.flagLiveFlash,
    pushMemberActivity: memberActivitySignals.pushMemberActivity,
    markProductDetailObserved: productDetailObserver.markProductDetailObserved,
    markReflectionsObserved: reflectionObserver.markReflectionsObserved,
    flagMemberChanges,
    pushMemberActivitiesFromCollabs: collabSignals.pushMemberActivitiesFromCollabs,
    flagCollabArrivalsAndAncestors: collabSignals.flagCollabArrivalsAndAncestors,
  };
}
